import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, loader, pagination, textColumn } from '@/ui/components';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import * as qs from '@/ui/queryState';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import { filterContainer, searchFilter } from '@/ui/components/filters';
import { SortDirection } from '@/ui/types';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { ChecklistList, ChecklistRecord } from '@/entities/checklist';
import { emptyList } from '@/services/api';
import { list as getChecklistList } from '@/services/api/resources/checklist';
import { summaryChecklistsDetailPageLink } from '@/ui/pages/analytics/SummaryChecklists/SummaryChecklistsDetailPage';

export const SummaryChecklistsPage = defineComponent({
  name: 'SummaryChecklistsPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const search = qs.string('q', '');
    const { sort, dir } = useSorting('createdAt', SortDirection.Ascending);

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      sort: sort.value,
      dir: dir.value,
      search: search.value,
      category: [],
      page: page.value,
      perPage: perPage.value,
      status: null,
    });

    // get list task
    const list = ref<ChecklistList>(emptyList());
    const getList = useTask(
      () => getChecklistList(query()),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );

    watch([page, perPage, search], getList.run);

    return function render() {
      return h('div', [
        h('div', [
          h('div', { class: 'columns legacy-wrapper' }, [
            filterContainer([
              searchFilter(search, resetPagination),
            ]),
            h('div', { class: 'column legacy' }, [
              box([
                loader('div', getList, [
                  h(AnalyticsTable, {
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                    },
                    scopedSlots: {
                      default: () => [
                        textColumn({
                          customKey: 'title',
                          field: 'title',
                          label: t('common.title'),
                        }, {
                          default: ({ row }: { row: ChecklistRecord }) =>
                            row.id && row.authorizedTo.view
                              ? [summaryChecklistsDetailPageLink(row.data.title, row.id)]
                              : row.data.title,
                        }),
                        textColumn({
                          customKey: 'description',
                          field: 'description',
                          label: t('common.description'),
                        }, {
                          default: ({ row }: { row: ChecklistRecord }) => row.data.description,
                        }),
                      ],
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});
